import * as React from 'react';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

export interface TenantOption {
  id: string;
  name: string;
  /** Short code (e.g. "ACME"), shown beside the name in the trigger pill. */
  code?: string;
  /** Optional environment label (e.g. "staging"). Rendered as a chip. */
  environment?: string;
}

export interface TenantSwitcherProps {
  tenants: TenantOption[];
  /** Currently active tenant id. */
  currentTenantId?: string;
  /** Invoked when the user picks a different tenant. */
  onTenantChange?: (tenant: TenantOption) => void;
  /** Placeholder text when no tenant is selected. */
  placeholder?: string;
  /** Show a search input above the list when tenant count exceeds this. */
  searchThreshold?: number;
  className?: string;
}

/**
 * TenantSwitcher — pill-style dropdown for switching tenants/workspaces.
 *
 * Slot-driven: consumer supplies the tenant list and the selection handler.
 * The kit owns the visual treatment (pill trigger, searchable list, active
 * checkmark) so every ZAFROI app feels the same when context-switching.
 */
export function TenantSwitcher({
  tenants,
  currentTenantId,
  onTenantChange,
  placeholder = 'Select tenant',
  searchThreshold = 6,
  className,
}: TenantSwitcherProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const current = tenants.find((t) => t.id === currentTenantId);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return tenants;
    const needle = query.trim().toLowerCase();
    return tenants.filter(
      (t) =>
        t.name.toLowerCase().includes(needle) ||
        t.code?.toLowerCase().includes(needle),
    );
  }, [tenants, query]);

  const showSearch = tenants.length >= searchThreshold;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex max-w-[220px] items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            className,
          )}
          aria-label="Switch tenant"
        >
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="truncate">{current?.name ?? placeholder}</span>
          {current?.code && (
            <span className="rounded bg-muted px-1.5 py-px font-mono text-[10px] uppercase text-muted-foreground">
              {current.code}
            </span>
          )}
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        {showSearch && (
          <div className="border-b border-border p-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tenants…"
              className="h-8 text-xs"
              autoFocus
            />
          </div>
        )}
        <div className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-6 text-center text-xs text-muted-foreground">
              No matching tenants
            </p>
          ) : (
            filtered.map((tenant) => {
              const active = tenant.id === currentTenantId;
              return (
                <button
                  key={tenant.id}
                  type="button"
                  onClick={() => {
                    onTenantChange?.(tenant);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted focus:bg-muted focus:outline-none',
                    active && 'bg-muted',
                  )}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-muted text-[10px] font-semibold uppercase text-muted-foreground">
                    {tenant.code ?? tenant.name.slice(0, 2)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {tenant.name}
                  </span>
                  {tenant.environment && (
                    <span className="rounded bg-warning/10 px-1.5 py-px text-[10px] uppercase text-warning">
                      {tenant.environment}
                    </span>
                  )}
                  {active && (
                    <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
